/**
 * ========================================
 * COLOR SWATCHES - PRODUCTION v2.2
 * ========================================
 * Smart color switching with configurable behavior (Theme Settings > Color Swatches):
 *
 * Modes:
 * - AUTO: AJAX for simple products, page reload for products with Kaching bundles (default)
 * - AJAX: Force instant AJAX switching (breaks Kaching bundles, use when Kaching is disabled)
 * - RELOAD: Force page reload for all products (safest, works with all apps)
 *
 * Features:
 * + Theme settings toggle for switching modes
 * + Size selection persistence across color changes (both AJAX and page reload modes)
 * + Uses sessionStorage to remember size across page reloads
 * + Product caching for instant revisits
 * + Full tracking integration (GA4, GTM, Klaviyo, Aimerce, Judge.me)
 * + Kaching bundle size synchronization (AJAX mode only)
 */

// Suppress harmless resource loading errors
window.addEventListener('error', function(e) {
  if (!e.target) return;
  
  const tag = e.target.tagName;
  const href = e.target.href || '';
  
  if (['SOURCE', 'TRACK', 'VIDEO', 'AUDIO'].includes(tag)) {
    e.stopPropagation();
    e.preventDefault();
    return false;
  }
  
  if (tag === 'LINK' && href.includes('undefined')) {
    e.target.remove();
    e.stopPropagation();
    e.preventDefault();
    return false;
  }
  
  if (['SCRIPT', 'IMG', 'IFRAME'].includes(tag)) {
    e.stopPropagation();
    e.preventDefault();
    return false;
  }
  
  if (e.message && e.message.includes('Identifier') && e.message.includes('already been declared')) {
    e.stopPropagation();
    e.preventDefault();
    return false;
  }
}, true);

window.currentProductContext = null;
window._colorSwatchCache = window._colorSwatchCache || {}; // Cache for visited products

// Get color swatch mode from theme settings (auto, ajax, or reload)
const getSwatchMode = () => {
  const mode = window.colorSwatchSettings?.mode || 'auto';
  return mode;
};

// Save selected size to sessionStorage for persistence across page reloads
const saveSelectedSize = (size) => {
  try {
    sessionStorage.setItem('selectedProductSize', size);
    window.lastSelectedSize = size;
  } catch (e) {
    console.error('Failed to save size to sessionStorage:', e);
  }
};

// Get saved size from sessionStorage
const getSavedSize = () => {
  try {
    return sessionStorage.getItem('selectedProductSize') || null;
  } catch (e) {
    return null;
  }
};

/**
 * Sync Kaching Bundle size selectors with main product size selection
 * NOTE: Only used for AJAX swaps (products WITHOUT bundles)
 * Products WITH bundles use page reload, so no sync needed
 * This ensures that when a user selects a size in the bundle items,
 * it uses the correct variant from the current color product
 */
function syncKachingBundleSizes(selectedSize, variantId) {
  try {
    const kachingBundle = document.querySelector('kaching-bundle');
    if (!kachingBundle) return;

    // Find all size selectors within the Kaching bundle
    const bundleSelectors = kachingBundle.querySelectorAll('select[name*="properties"], select.bundle-variant-select, .kaching-variant-selector select');

    bundleSelectors.forEach(select => {
      // Try to find and select the matching size option
      const options = Array.from(select.options);
      const matchingOption = options.find(opt =>
        opt.text.includes(selectedSize) || opt.value.includes(String(variantId))
      );

      if (matchingOption) {
        select.value = matchingOption.value;
        select.dispatchEvent(new Event('change', { bubbles: true }));
        console.log('📏 Synced Kaching bundle selector to:', selectedSize);
      }
    });

    // Also sync any radio button selectors
    const bundleRadios = kachingBundle.querySelectorAll('input[type="radio"][name*="Size"], input[type="radio"][name*="variant"]');
    bundleRadios.forEach(radio => {
      if (radio.value === selectedSize || radio.dataset.variantId === String(variantId)) {
        radio.checked = true;
        radio.dispatchEvent(new Event('change', { bubbles: true }));
        console.log('📏 Synced Kaching bundle radio to:', selectedSize);
      }
    });
  } catch (e) {
    console.error('Kaching size sync error:', e);
  }
}

/**
 * Set up observers to sync sizes when Kaching bundle renders
 * NOTE: Only used for AJAX swaps (products WITHOUT bundles)
 */
function setupKachingBundleSizeSync(productData) {
  try {
    const kachingBundle = document.querySelector('kaching-bundle');
    if (!kachingBundle) return;

    // Use MutationObserver to watch for when Kaching bundle adds its UI
    const observer = new MutationObserver(() => {
      // Check if bundle has rendered its content
      const bundleItems = kachingBundle.querySelectorAll('.kaching-bundle-item, [class*="bundle"], [data-bundle-item]');

      if (bundleItems.length > 0) {
        console.log('👀 Kaching bundle rendered, syncing sizes...');

        // Get currently selected size from main selector
        const masterSelect = document.querySelector('select[name="id"][data-product-select]');
        if (masterSelect) {
          const selectedVariantId = masterSelect.value;
          const matchingVariant = productData.variants.find(v => v.id == selectedVariantId);

          if (matchingVariant && window.lastSelectedSize) {
            // Apply the last selected size to bundle
            setTimeout(() => {
              syncKachingBundleSizes(window.lastSelectedSize, selectedVariantId);
            }, 100);
          }
        }

        // Listen to changes within bundle items
        const bundleSelectors = kachingBundle.querySelectorAll('select, input[type="radio"]');
        bundleSelectors.forEach(selector => {
          selector.addEventListener('change', function() {
            console.log('📦 Bundle selector changed:', this.value);
            // Store the bundle's selection
            window._kachingBundleSelection = this.value;
          });
        });

        // Stop observing once we've set up the listeners
        observer.disconnect();
      }
    });

    // Start observing the bundle element
    observer.observe(kachingBundle, {
      childList: true,
      subtree: true,
      attributes: true
    });

    // Cleanup after 5 seconds if bundle hasn't rendered
    setTimeout(() => observer.disconnect(), 5000);

  } catch (e) {
    console.error('Kaching bundle observer error:', e);
  }
}

function initColorSwatches() {
  const container = document.querySelector("#ColorSwatches");
  if (!container) return;

  const newContainer = container.cloneNode(true);
  container.parentNode.replaceChild(newContainer, container);

  newContainer.addEventListener("click", async (e) => {
    const btn = e.target.closest(".color-swatch");
    if (!btn) return;

    const handle = btn.dataset.handle;
    if (!handle || btn.classList.contains("is-active")) return;

    // Save currently selected size before switching colors
    const currentSize = document.querySelector('input[data-variant-input][name="Size"]:checked')?.value ||
                        document.querySelector('select[name="id"][data-product-select] option:checked')?.textContent?.trim();
    if (currentSize) {
      saveSelectedSize(currentSize);
      console.log('💾 Saved size selection:', currentSize);
    }

    btn.classList.add("loading");

    try {
      const currentSection =
        document.querySelector(".product-single") ||
        document.querySelector(".product-section") ||
        document.querySelector(".product") ||
        document.querySelector("[class*='product-container']") ||
        document.querySelector("[data-section-type*='product']") ||
        document.querySelector("main .shopify-section") ||
        document.querySelector("#MainContent");

      if (!currentSection) throw new Error("Product section not found");

      // Check if we have this product cached
      if (window._colorSwatchCache[handle]) {
        console.log('⚡ Using cached data for:', handle);
        const cachedData = window._colorSwatchCache[handle];
        const mode = getSwatchMode();

        // Determine if we should reload based on mode setting
        const shouldReload = mode === 'reload' || (mode === 'auto' && cachedData.hasBundle);

        // Check if we should do page reload
        if (shouldReload) {
          console.log(`⚠️ Mode: ${mode} - doing page reload`);

          // Get logo from header
          const headerLogo = document.querySelector('header img[src*="logo"], .header__logo img, [class*="logo"] img');
          const logoUrl = headerLogo ? headerLogo.src : '';

          // Create branded loading overlay
          const loadingOverlay = document.createElement('div');
          loadingOverlay.style.cssText = `
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: rgba(255, 255, 255, 0.95);
            z-index: 99999;
            display: flex;
            align-items: center;
            justify-content: center;
            opacity: 0;
            transition: opacity 0.2s ease;
          `;

          loadingOverlay.innerHTML = `
            <div style="text-align: center;">
              ${logoUrl ? `<img src="${logoUrl}" alt="Loading" style="width: 180px; height: auto; animation: pulse 1.5s ease-in-out infinite;" />` : '<div style="font-size: 18px; color: #142559;">Loading...</div>'}
            </div>
            <style>
              @keyframes pulse {
                0%, 100% { opacity: 0.6; transform: scale(1); }
                50% { opacity: 1; transform: scale(1.05); }
              }
            </style>
          `;

          document.body.appendChild(loadingOverlay);
          setTimeout(() => { loadingOverlay.style.opacity = '1'; }, 10);
          setTimeout(() => { window.location.href = `/products/${handle}`; }, 300);
          return;
        }

        // Use instant AJAX cached version
        console.log(`🚀 Instant cache switch (mode: ${mode})`);
        const fragment = document.createDocumentFragment();
        const temp = document.createElement('div');
        temp.innerHTML = cachedData.html;
        Array.from(temp.firstElementChild.childNodes).forEach(node => {
          fragment.appendChild(node.cloneNode(true));
        });

        currentSection.innerHTML = '';
        currentSection.appendChild(fragment);

        // Update UI and reinitialize
        setTimeout(() => {
          const allSwatches = document.querySelectorAll('.color-swatch');
          allSwatches.forEach(swatch => {
            if (swatch.dataset.productId == cachedData.productData.id) {
              swatch.classList.add('is-active');
            } else {
              swatch.classList.remove('is-active');
            }
          });
        }, 50);

        window.history.pushState({ handle }, "", `/products/${handle}`);
        const titleMatch = cachedData.html.match(/<title>(.*?)<\/title>/);
        if (titleMatch) document.title = titleMatch[1];

        setTimeout(() => reinitializeAllTracking(cachedData.productData), 100);
        setTimeout(() => window.scrollTo({ top: currentSection.offsetTop - 100, behavior: "smooth" }), 200);

        btn.classList.remove("loading");
        return;
      }

      // Save current Kaching bundle as backup BEFORE fetching
      const currentKachingBundle = currentSection.querySelector('kaching-bundle');
      const currentBundleBackup = currentKachingBundle ? currentKachingBundle.outerHTML : null;

      const productDataPromise = fetch(`/products/${handle}.js`).then(r => r.json());
      const response = await fetch(`/products/${handle}`);
      if (!response.ok) throw new Error(`Fetch failed: ${response.status}`);

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, "text/html");

      let newSection;
      if (currentSection.id) newSection = doc.querySelector(`#${currentSection.id}`);
      if (!newSection && currentSection.className) {
        const firstClass = currentSection.className.split(' ')[0];
        newSection = doc.querySelector(`.${firstClass}`);
      }
      if (!newSection) {
        newSection = 
          doc.querySelector(".product-single") ||
          doc.querySelector(".product-section") ||
          doc.querySelector(".product") ||
          doc.querySelector("[class*='product-container']") ||
          doc.querySelector("[data-section-type*='product']") ||
          doc.querySelector("main .shopify-section") ||
          doc.querySelector("#MainContent");
      }

      if (!newSection) throw new Error("New section not found");

const productData = await productDataPromise;
const mode = getSwatchMode();

// Determine if we should reload based on mode setting and bundle presence
const shouldReload = mode === 'reload' || (mode === 'auto' && currentBundleBackup);

// Check if we should do page reload
if (shouldReload) {
  // Do a full page reload for the new product
  console.log(`⚠️ Mode: ${mode} - doing page reload for fresh fetch`);

  // Get logo from header
  const headerLogo = document.querySelector('header img[src*="logo"], .header__logo img, [class*="logo"] img');
  const logoUrl = headerLogo ? headerLogo.src : '';

  // Create branded loading overlay
  const loadingOverlay = document.createElement('div');
  loadingOverlay.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.95);
    z-index: 99999;
    display: flex;
    align-items: center;
    justify-content: center;
    opacity: 0;
    transition: opacity 0.2s ease;
  `;

  loadingOverlay.innerHTML = `
    <div style="text-align: center;">
      ${logoUrl ? `<img src="${logoUrl}" alt="Loading" style="width: 180px; height: auto; animation: pulse 1.5s ease-in-out infinite;" />` : '<div style="font-size: 18px; color: #142559;">Loading...</div>'}
    </div>
    <style>
      @keyframes pulse {
        0%, 100% { opacity: 0.6; transform: scale(1); }
        50% { opacity: 1; transform: scale(1.05); }
      }
    </style>
  `;

  document.body.appendChild(loadingOverlay);

  // Fade in the overlay
  setTimeout(() => {
    loadingOverlay.style.opacity = '1';
  }, 10);

  // Reload to the new product URL
  setTimeout(() => {
    window.location.href = `/products/${handle}`;
  }, 300);

  // Stop further execution since we're reloading
  return;
}

// AJAX mode - proceed with DOM swap
const fragment = document.createDocumentFragment();
Array.from(newSection.childNodes).forEach(node => {
  if (node.nodeType === 1 && node.tagName === 'SCRIPT' && !node.src) return;
  fragment.appendChild(node.cloneNode(true));
});

currentSection.innerHTML = '';
currentSection.appendChild(fragment);

// Check if new page has a bundle (for caching purposes)
const newPageHasBundle = !!newSection.querySelector('kaching-bundle');

// Cache this product for instant loading next time
window._colorSwatchCache[handle] = {
  html: newSection.outerHTML,
  productData: productData,
  hasBundle: newPageHasBundle || !!currentBundleBackup
};
console.log('💾 Cached product data for:', handle, '(has bundle:', (newPageHasBundle || !!currentBundleBackup), ')');

      setTimeout(() => {
        const allSwatches = document.querySelectorAll('.color-swatch');
        allSwatches.forEach(swatch => {
          if (swatch.dataset.productId == productData.id) {
            swatch.classList.add('is-active');
          } else {
            swatch.classList.remove('is-active');
          }
        });
      }, 50);

      window.history.pushState({ handle }, "", `/products/${handle}`);
      const newPageTitle = doc.querySelector("title");
      if (newPageTitle) document.title = newPageTitle.textContent;

      setTimeout(() => reinitializeAllTracking(productData), 100);
      setTimeout(() => window.scrollTo({ top: currentSection.offsetTop - 100, behavior: "smooth" }), 200);

    } catch (err) {
      console.error("Color swatch switch failed:", err);
      window.location.href = `/products/${handle}`;
    } finally {
      btn.classList.remove("loading");
    }
  });
}

/**
 * Reinitialize all tracking scripts and product UI
 * NOTE: This only runs for AJAX mode
 * AUTO/RELOAD modes with bundles trigger page reload, which naturally reinitializes everything
 */
function reinitializeAllTracking(productData) {
  console.log('🔄 Reinitializing tracking for AJAX mode');
  // Get saved size from sessionStorage (persists across page reloads)
  const savedSize = getSavedSize();
  const lastSelectedSize = savedSize || window.lastSelectedSize || null;

  // Find the variant that matches the remembered size, fallback to first variant
  let variant = lastSelectedSize
    ? (productData.variants.find(v => v.title === lastSelectedSize) || productData.variants[0])
    : productData.variants[0];

  if (lastSelectedSize) {
    console.log('🔄 Restoring saved size:', lastSelectedSize);
  }
  
  const currency = Shopify?.currency?.active || "USD";

  window.currentProductContext = {
    productId: productData.id,
    variantId: variant.id,
    productTitle: productData.title,
    variantTitle: variant.title,
    price: variant.price / 100,
    compareAtPrice: variant.compare_at_price ? variant.compare_at_price / 100 : null,
    vendor: productData.vendor || "",
    productType: productData.type || "",
    tags: productData.tags || [],
    url: window.location.href,
    imageUrl: productData.featured_image,
    currency: currency
  };

  initColorSwatches();

  // CRITICAL: Re-initialize Motion theme's variant system properly
  setTimeout(() => {
    const productSection = document.querySelector('[data-section-type="product"]');
    const sectionId = productSection?.dataset.sectionId;
    
    if (productSection && sectionId) {
      const variantJsonEl = productSection.querySelector('[data-variant-json]');
      if (variantJsonEl) {
        variantJsonEl.textContent = JSON.stringify(productData.variants);
      }
      
      try {
        if (window.sections && window.sections[sectionId]) {
          if (typeof window.sections[sectionId].onUnload === 'function') {
            window.sections[sectionId].onUnload();
          }
          delete window.sections[sectionId];
        }
        
        if (window.theme && window.theme.Product) {
          window.sections = window.sections || {};
          window.sections[sectionId] = new window.theme.Product(productSection);
        }
      } catch (e) {
        console.error('Theme variant init failed:', e);
      }
    }
    
    // Fallback + size persistence
    setTimeout(() => {
      const masterSelect = document.querySelector('select[name="id"][data-product-select]');
      const sizeInputs = document.querySelectorAll('input[data-variant-input][name="Size"]');

      // RESTORE the remembered size selection in UI
      if (lastSelectedSize && sizeInputs.length > 0) {
        const targetInput = Array.from(sizeInputs).find(input => input.value === lastSelectedSize);
        if (targetInput) {
          targetInput.checked = true;
          if (masterSelect) {
            const matchingVariant = productData.variants.find(v => v.title === lastSelectedSize);
            if (matchingVariant) {
              masterSelect.value = matchingVariant.id;
              masterSelect.dispatchEvent(new Event('change', { bubbles: true }));
            }
          }
        }
      }

      // Sync main size selector with Kaching bundle size selectors
      if (masterSelect && sizeInputs.length > 0) {
        sizeInputs.forEach(input => {
          input.addEventListener('change', function() {
            const selectedSize = this.value;
            const matchingVariant = productData.variants.find(v => v.title === selectedSize);

            if (matchingVariant) {
              masterSelect.value = matchingVariant.id;
              masterSelect.dispatchEvent(new Event('change', { bubbles: true }));

              window.currentProductContext.variantId = matchingVariant.id;
              window.currentProductContext.variantTitle = matchingVariant.title;
              window.currentProductContext.price = matchingVariant.price / 100;

              // Save to both memory and sessionStorage
              saveSelectedSize(selectedSize);

              // Sync with Kaching bundle internal selectors
              syncKachingBundleSizes(selectedSize, matchingVariant.id);
            }
          });
        });
      }

      if (masterSelect) {
        masterSelect.addEventListener('change', function() {
          const selectedVariantId = this.value;
          const matchingVariant = productData.variants.find(v => v.id == selectedVariantId);

          if (matchingVariant) {
            window.currentProductContext.variantId = matchingVariant.id;
            window.currentProductContext.variantTitle = matchingVariant.title;
            window.currentProductContext.price = matchingVariant.price / 100;

            // Save to both memory and sessionStorage
            saveSelectedSize(matchingVariant.title);

            // Sync with Kaching bundle internal selectors
            syncKachingBundleSizes(matchingVariant.title, matchingVariant.id);
          }
        });
      }

      // Set up observers for Kaching bundle size changes
      setupKachingBundleSizeSync(productData);
    }, 100);
  }, 250);

  // Shopify features
  try {
    if (typeof Shopify !== 'undefined') {
      if (Shopify.PaymentButton) Shopify.PaymentButton.init();
      if (Shopify.ProductForm) Shopify.ProductForm.init();
    }
  } catch (e) { }

  // Google Analytics 4
  if (typeof gtag === "function") {
    gtag("event", "view_item", {
      currency: currency,
      value: window.currentProductContext.price.toFixed(2),
      items: [{
        item_id: String(variant.id),
        item_name: productData.title,
        item_variant: variant.title,
        item_brand: window.currentProductContext.vendor,
        item_category: window.currentProductContext.productType,
        price: window.currentProductContext.price.toFixed(2),
        quantity: 1
      }]
    });
  }

  // Google Tag Manager
  if (typeof dataLayer !== 'undefined') {
    dataLayer.push({
      event: 'view_item',
      ecommerce: {
        currency: currency,
        value: window.currentProductContext.price.toFixed(2),
        items: [{
          item_id: String(variant.id),
          item_name: productData.title,
          item_variant: variant.title,
          item_brand: window.currentProductContext.vendor,
          item_category: window.currentProductContext.productType,
          price: window.currentProductContext.price.toFixed(2),
          quantity: 1
        }]
      }
    });
  }

  // Klaviyo
  if (window._learnq) {
    window._learnq.push(["track", "Viewed Product", {
      ProductID: variant.id,
      ProductName: productData.title,
      ImageURL: productData.featured_image,
      URL: window.currentProductContext.url,
      Price: window.currentProductContext.price,
      CompareAtPrice: window.currentProductContext.compareAtPrice,
      Categories: window.currentProductContext.tags,
      Brand: window.currentProductContext.vendor,
      ProductType: window.currentProductContext.productType
    }]);
  }

  // Aimerce
  setTimeout(() => {
    try {
      window._aimTrack = window._aimTrack || [];
      window._aimTrack.push(['aim_view_item', {
        id: String(productData.id),
        title: productData.title,
        type: window.currentProductContext.productType,
        untranslatedTitle: productData.title,
        url: window.currentProductContext.url,
        vendor: window.currentProductContext.vendor,
        selectedVariant: {
          id: String(variant.id),
          image: {
            url: productData.featured_image,
            altText: productData.title
          },
          price: {
            amount: String(window.currentProductContext.price.toFixed(2)),
            currencyCode: currency
          },
          product: {
            id: String(productData.id),
            title: productData.title,
            type: window.currentProductContext.productType,
            untranslatedTitle: productData.title,
            url: window.currentProductContext.url,
            vendor: window.currentProductContext.vendor
          },
          sku: variant.sku || null,
          title: variant.title,
          untranslatedTitle: variant.title
        }
      }]);
    } catch (e) {
      console.error('Aimerce tracking error:', e);
    }
  }, 500);

  // Judge.me reviews
  if (typeof jdgm !== 'undefined') {
    try {
      document.querySelectorAll('link.jdgm-stylesheet[href*="undefined"]').forEach(el => el.remove());
      if (jdgm.customizePreviewBadge) jdgm.customizePreviewBadge();
      const reviewWidgets = document.querySelectorAll('[data-jdgm-widget]');
      if (reviewWidgets.length > 0 && jdgm.refresh) jdgm.refresh();
    } catch (e) { }
  }

  // Kaching Cart
  if (window.KachingCart && typeof window.KachingCart.refresh === 'function') {
    try { window.KachingCart.refresh(); } catch (e) { }
  }

  // Note: No Kaching bundle reinitialization needed here since products with bundles
  // trigger a full page reload, which naturally reinitializes everything

  // Raleon Loyalty
  if (window.RaleonLoyalty && typeof window.RaleonLoyalty.refresh === 'function') {
    try { window.RaleonLoyalty.refresh(); } catch (e) { }
  }

  setupAddToCartTracking();

  document.dispatchEvent(new CustomEvent('product:loaded', {
    bubbles: true,
    detail: { source: 'color-swatches', product: productData, variant: variant }
  }));

  setTimeout(() => window.dispatchEvent(new Event('resize')), 50);
}

function setupAddToCartTracking() {
  const addToCartForms = document.querySelectorAll('form[action*="/cart/add"]');
  
  addToCartForms.forEach(form => {
    const newForm = form.cloneNode(true);
    form.parentNode.replaceChild(newForm, form);
    newForm.addEventListener('submit', function() {
      if (window.currentProductContext) trackAddToCart(window.currentProductContext);
    });
  });

  document.addEventListener('product:added-to-cart', function() {
    if (window.currentProductContext) trackAddToCart(window.currentProductContext);
  });

  document.addEventListener('cart:item-added', function() {
    if (window.currentProductContext) trackAddToCart(window.currentProductContext);
  });
}

/**
 * Track add-to-cart events across all analytics platforms
 * NOTE: Runs for both AJAX swaps and page reloads (event listeners persist)
 */
function trackAddToCart(context) {
  const { variantId, productTitle, variantTitle, price, vendor, productType, imageUrl, currency } = context;

  // Aimerce add to cart
  try {
    window._aimTrack = window._aimTrack || [];
    window._aimTrack.push(['aim_add_to_cart', {
      id: String(context.productId),
      title: productTitle,
      type: productType,
      vendor: vendor,
      selectedVariant: {
        id: String(variantId),
        title: variantTitle,
        price: {
          amount: String(price.toFixed(2)),
          currencyCode: currency
        }
      }
    }]);
  } catch (e) {
    console.error('Aimerce add-to-cart tracking error:', e);
  }

  // Google Analytics 4
  if (typeof gtag === "function") {
    gtag("event", "add_to_cart", {
      currency: currency,
      value: price.toFixed(2),
      items: [{
        item_id: String(variantId),
        item_name: productTitle,
        item_variant: variantTitle,
        item_brand: vendor,
        item_category: productType,
        price: price.toFixed(2),
        quantity: 1
      }]
    });
  }

  // Google Tag Manager
  if (typeof dataLayer !== 'undefined') {
    dataLayer.push({
      event: 'add_to_cart',
      ecommerce: {
        currency: currency,
        value: price.toFixed(2),
        items: [{
          item_id: String(variantId),
          item_name: productTitle,
          item_variant: variantTitle,
          item_brand: vendor,
          item_category: productType,
          price: price.toFixed(2),
          quantity: 1
        }]
      }
    });
  }

  // Klaviyo
  if (window._learnq) {
    window._learnq.push(["track", "Added to Cart", {
      ProductID: variantId,
      ProductName: productTitle,
      ItemPrice: price,
      Quantity: 1,
      CheckoutURL: window.location.origin + '/checkout',
      ImageURL: imageUrl,
      ProductType: productType,
      Brand: vendor
    }]);
  }
}

window.addEventListener("popstate", () => location.reload());

// Restore saved size selection on page load (for page reloads)
function restoreSavedSizeOnPageLoad() {
  const savedSize = getSavedSize();
  if (!savedSize) return;

  console.log('🔄 Restoring size from page reload:', savedSize);

  // Wait for page to be fully loaded
  setTimeout(() => {
    // Try to find and select the saved size
    const sizeInputs = document.querySelectorAll('input[data-variant-input][name="Size"]');
    const masterSelect = document.querySelector('select[name="id"][data-product-select]');

    if (sizeInputs.length > 0) {
      // Radio button variant selector
      const targetInput = Array.from(sizeInputs).find(input => input.value === savedSize);
      if (targetInput && !targetInput.checked) {
        targetInput.checked = true;
        targetInput.dispatchEvent(new Event('change', { bubbles: true }));
        console.log('✅ Restored size selection:', savedSize);
      }
    } else if (masterSelect) {
      // Dropdown variant selector
      const options = Array.from(masterSelect.options);
      const matchingOption = options.find(opt => opt.text.includes(savedSize));
      if (matchingOption && masterSelect.value !== matchingOption.value) {
        masterSelect.value = matchingOption.value;
        masterSelect.dispatchEvent(new Event('change', { bubbles: true }));
        console.log('✅ Restored size selection:', savedSize);
      }
    }

    // Also update window.lastSelectedSize for consistency
    window.lastSelectedSize = savedSize;
  }, 250);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", initColorSwatches);
  document.addEventListener("DOMContentLoaded", setupAddToCartTracking);
  document.addEventListener("DOMContentLoaded", restoreSavedSizeOnPageLoad);
} else {
  initColorSwatches();
  setupAddToCartTracking();
  restoreSavedSizeOnPageLoad();
}